<template>
    <div class="login-container">
      <div class="row g-0 h-100">
        <!-- Partie illustration/explications -->
        <div class="col-lg-6 d-none d-lg-flex flex-column justify-content-center p-5 bg-primary text-white">
          <div class="login-info">
            <h1 class="display-5 fw-bold mb-4">Bienvenue sur votre panel freelance</h1>
            
            <div class="steps-container">
              <div class="step" :class="{ 'active': currentStep === 1 }">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h5>Entrez votre email</h5>
                  <p>Nous vous enverrons un code de vérification sécurisé</p>
                </div>
              </div>
              
              <div class="step" :class="{ 'active': currentStep === 2 }">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h5>Saisissez le code</h5>
                  <p>Entrez le code à 6 chiffres reçu par email</p>
                </div>
              </div>
            </div>
            
            <div class="mt-4">
              <p><i class="fas fa-shield-alt me-2"></i> Ce panel vous permet de gerer votre profil et répondre aux offres</p>
              <p><i class="fas fa-question-circle me-2"></i> Problème de connexion ? <a href="mailto:juste-mikala@asdecode.com" class="text-white">Contactez-nous</a></p>
            </div>
          </div>
        </div>
        
        <!-- Partie formulaire -->
        <div class="col-lg-6 d-flex flex-column justify-content-center p-5">
          <div class="login-form-container">
            <div class="text-center mb-4">
              <img src="../assets/logo-noir.png" alt="Logo asdecode" class="logo mb-3" >
              <h2 class="mb-1">Connexion sécurisée</h2>
              <p class="text-muted">Accédez à votre espace personnel</p>
            </div>
            
            <!-- Étape 1 : Email -->
            <transition name="fade-slide" mode="out-in">
              <div v-if="currentStep === 1" key="step1" class="step-form">
                <form @submit.prevent="sendVerificationCode" class="needs-validation" novalidate>
                  <div class="mb-4">
                    <label for="email" class="form-label">Adresse email</label>
                    <input 
                      v-model="email" 
                      type="email" 
                      class="form-control form-control-lg rounded-pill" 
                      id="email" 
                      required
                      placeholder="votre@email.com"
                    >
                    <div class="invalid-feedback">
                      Veuillez entrer une adresse email valide
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    class="btn btn-primary btn-lg w-100 rounded-pill login-btn"
                    :disabled="loading"
                  >
                    <span v-if="!loading">
                      <i class="fas fa-paper-plane me-2"></i> Envoyer le code
                    </span>
                    <span v-else>
                      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Envoi en cours...
                    </span>
                  </button>
                </form>
              </div>
              
              <!-- Étape 2 : Code de vérification -->
              <div v-else key="step2" class="step-form">
                <div class="text-center">
                  <div class="verification-sent mb-3">
                    <i class="fas fa-envelope-open-text fa-3x text-primary"></i>
                    <p class="mt-2">Code envoyé à <strong>{{ email }}</strong></p>
                  </div>
                </div>
                
                <form @submit.prevent="verifyCode" class="needs-validation" novalidate>
                  <div class="mb-4">
                    <label class="form-label">Code de vérification</label>
                    <div class="verification-code-input">
                      <input 
                        v-for="i in 6" 
                        :key="i" 
                        v-model="verificationCode[i-1]" 
                        @input="handleCodeInput(i-1, $event)"
                        @keydown.delete="handleCodeDelete(i-1, $event)"
                        type="text" 
                        maxlength="1" 
                        class="form-control form-control-lg text-center" 
                        ref="codeInputs"
                        required
                      >
                    </div>
                    <div class="d-flex justify-content-between mt-2">
                      <small class="text-muted">Code à 6 chiffres</small>
                      <button type="button" class="btn btn-link p-0" @click="resendCode" :disabled="countdown > 0">
                        <small>Renvoyer le code</small>
                        <small v-if="countdown > 0">dans {{countdown}}</small> 
                      </button>
                    </div>
                  </div>
                  
                  <button v-if="loading" 
                    type="submit" 
                    class="btn btn-primary btn-lg w-100 rounded-pill login-btn"
                    :disabled="loading || !isCodeComplete"
                  >
                    <span>
                      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Chargement...
                    </span>
                  </button>
                </form>
                
                <div class="text-center mt-3">
                  <button type="button" class="btn btn-link" @click="backToStep1">
                    <i class="fas fa-arrow-left me-1"></i> Modifier l'email
                  </button>
                </div>
              </div>
            </transition>
            
            <div class="text-center mt-4">
              <p class="text-muted">Pas encore de compte ? <a :href="this.$store.getters.apiUrl+'/devenir-membre'">Créer un compte</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios'
  
  export default {
    name: 'LoginView',
    data() {
      return {
        currentStep: 1,
        email: '',
        verificationCode: ['', '', '', '', '', ''],
        loading: false,
        countdown: 0,
        countdownInterval: null
      }
    },
    computed: {
      isCodeComplete() {
        return this.verificationCode.every(digit => digit !== '')
      }
    },
    methods: {
      async sendVerificationCode() {
        if (!this.validateEmail(this.email)) {
          return
        }
        
        this.loading = true
        
        try {
          // Simuler l'envoi du code
          await axios.post('/api/membership/send-code', { email: this.email })
          
          // Début du compte à rebours pour le renvoi (60 secondes)
          this.startCountdown()
          
          // Passer à l'étape 2
          this.currentStep = 2
        } catch (error) {
            this.$toast.add({
                severity: 'error',
                summary: 'Probleme de connexion',
                detail: 'Une erreur s\'est produite lors de l\'envoi du code !',
                life: 5000
            });
            console.error(error)
        } finally {
          this.loading = false
        }
      },
      
      async verifyCode() {
        if (!this.isCodeComplete) return
        
        this.loading = true
        const code = this.verificationCode.join('')
        
        try {
          // vérification du code
          const response = await axios.post('/api/membership/verify-code', {
            email: this.email,
            code: code
          })

          const profilResponse = await this.axios.post('/api/membership/profil', {
            email: this.email
          })
          
          // Connexion réussie
          const user = response.data.user;
          const token = response.data.token;

          const userDatas = {
            user: user,
            token: token
          }

          this.axios.defaults.headers.common = {
            'Authorization': 'Bearer ' + userDatas.token
          };

          await this.$store.commit('setAuthenticated', userDatas)
          await this.$store.commit('setMembership', profilResponse.data);

          //redirriger vers l'accueil
          this.$router.push('/')
          
        } catch (error) {
          console.log('error', error)
            this.$toast.add({
                severity: 'error',
                summary: 'erreur',
                detail: error.response.data.message,
                life: 5000
            });

            // Réinitialiser les champs du code
            this.verificationCode = ['', '', '', '', '', '']
            if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
              this.$refs.codeInputs[0].focus()
            }
        } finally {
          this.loading = false
        }
      },
      
      resendCode() {
        if (this.countdown > 0) return
        
        this.sendVerificationCode()
      },
      
      backToStep1() {
        this.currentStep = 1
        this.verificationCode = ['', '', '', '', '', '']
      },
      
      handleCodeInput(index, event) {
        const value = event.target.value
        
        // Ne garder que les chiffres
        if (!/^\d*$/.test(value)) {
          this.verificationCode[index] = ''
          return
        }
        
        this.verificationCode[index] = value
        
        // Passer au champ suivant si un chiffre a été saisi
        if (value && index < 5 && this.$refs.codeInputs[index + 1]) {
          this.$refs.codeInputs[index + 1].focus()
        }
        
        // Soumettre automatiquement si le code est complet
        if (this.isCodeComplete) {
          this.verifyCode()
        }
      },
      
      handleCodeDelete(index, event) {
        // Si le champ est vide et qu'on appuie sur suppr, aller au champ précédent
        if (event.target.value === '' && index > 0) {
          this.$refs.codeInputs[index - 1].focus()
        }
      },
      
      validateEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return re.test(email)
      },
      
      startCountdown() {
        this.countdown = 60
        clearInterval(this.countdownInterval)
        
        this.countdownInterval = setInterval(() => {
          if (this.countdown > 0) {
            this.countdown--
          } else {
            clearInterval(this.countdownInterval)
          }
        }, 1000)
      }
    },
    beforeUnmount() {
      clearInterval(this.countdownInterval)
    }
  }
  </script>
  
  <style scoped>
  .login-container {
    height: 100vh;
    background-color: #f8f9fa;
  }
  
  .login-info {
    max-width: 500px;
    margin: 0 auto;
  }
  
  .logo {
    max-height: 100px;
  }
  
  .login-form-container {
    max-width: 400px;
    margin: 0 auto;
    width: 100%;
  }
  
  .steps-container {
    margin: 2rem 0;
  }
  
  .step {
    display: flex;
    align-items: flex-start;
    padding: 1rem 0;
    opacity: 0.6;
    transition: all 0.3s ease;
  }
  
  .step.active {
    opacity: 1;
  }
  
  .step-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    font-weight: bold;
  }
  
  .step.active .step-number {
    background-color: white;
    color: var(--bs-primary);
  }
  
  .step-content h5 {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  
  .step-content p {
    margin-bottom: 0;
    font-size: 0.9rem;
    opacity: 0.8;
  }
  
  .verification-code-input {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }
  
  .verification-code-input input {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    border-radius: 10px !important;
    border: 2px solid #dee2e6;
    transition: all 0.2s;
  }
  
  .verification-code-input input:focus {
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
  }
  
  .verification-sent {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.5rem;
    display: inline-block;
  }
  
  .login-btn {
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
  }
  
  .login-btn::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 0;
    border-radius: 100%;
    transform: scale(1, 1) translate(-50%, -50%);
    transform-origin: 50% 50%;
  }
  
  .login-btn:focus:not(:active)::after {
    animation: ripple 1s ease-out;
  }
  
  @keyframes ripple {
    0% {
      transform: scale(0, 0);
      opacity: 0.5;
    }
    100% {
      transform: scale(20, 20);
      opacity: 0;
    }
  }
  
  /* Transitions entre les étapes */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.5s ease;
  }
  
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateX(30px);
  }
  
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateX(-30px);
  }
  
  @media (max-width: 992px) {
    .login-container {
      height: auto;
      min-height: 100vh;
    }
    
    .login-info {
      padding: 2rem 0;
    }
  }
  </style>